import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Signal, FileUp, BookOpen, Bot, Activity } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import HealthChip from './HealthChip';

const STAT_ICONS = {
  signals_created: Signal,
  articles_read: BookOpen,
  ai_queries: Bot,
  files_uploaded: FileUp,
};

const STAT_LABELS = {
  signals_created: 'Signals created',
  articles_read: 'Articles read',
  ai_queries: 'AI queries',
  files_uploaded: 'Files uploaded',
};

const fmtRel = (iso) => {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return '—';
  }
};

/**
 * iter165 — Per-user engagement drill-down. Renders as a Dialog when userId
 * is set. Fetches /admin/engagement/user/{id} and shows the 30-day activity
 * profile: counts, action breakdown, health status, recent event timeline.
 */
export default function UserEngagementDrawer({ userId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await api.get(`/admin/engagement/user/${userId}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load user activity');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, open]);

  const u = data?.user;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="user-engagement-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-400" />
            User Activity
          </DialogTitle>
          <DialogDescription>
            30-day engagement profile{u ? ` for ${u.full_name || u.email}` : ''}.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="h-40 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : !data ? (
          <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
            No data
          </div>
        ) : (
          <div className="space-y-4">
            {/* Identity + status */}
            <div className="flex items-start justify-between gap-3 border border-slate-700/60 rounded-lg p-3 bg-slate-900/40">
              <div>
                <div className="text-base font-semibold text-slate-100" data-testid="drawer-user-email">
                  {u.email}
                </div>
                <div className="text-xs text-slate-400">
                  {u.full_name || '—'} · {u.subscription_status} · {u.subscription_plan || 'free'}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Joined {u.created_at ? format(new Date(u.created_at), 'MMM d, yyyy') : '—'}
                  {' · '}Last active {fmtRel(u.last_active_at)}
                  {' · '}Last meaningful action {fmtRel(u.last_meaningful_action_at)}
                </div>
              </div>
              <HealthChip status={u.health_status} />
            </div>

            {/* 30-day counts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(data.counts_30d).map(([k, v]) => {
                const Icon = STAT_ICONS[k] || Activity;
                return (
                  <Card key={k} className="bg-slate-900/40">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wide">
                        <Icon className="h-3 w-3" />
                        {STAT_LABELS[k]}
                      </div>
                      <div className="text-2xl font-bold text-slate-100 mt-1" data-testid={`drawer-count-${k}`}>
                        {v}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Action breakdown */}
            {data.action_breakdown_30d?.length > 0 && (
              <div>
                <div className="text-xs uppercase text-slate-500 tracking-wide mb-2">
                  Action breakdown (last 30d)
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.action_breakdown_30d.map((row) => (
                    <Badge
                      key={row.action}
                      variant="secondary"
                      className="bg-slate-800 text-slate-200 border border-slate-700"
                    >
                      {row.action} <span className="ml-1 text-slate-400">×{row.count}</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Recent event timeline */}
            {data.recent_events?.length > 0 && (
              <div>
                <div className="text-xs uppercase text-slate-500 tracking-wide mb-2">
                  Recent events
                </div>
                <div className="border border-slate-700/60 rounded-lg divide-y divide-slate-800 max-h-52 overflow-y-auto">
                  {data.recent_events.map((ev, i) => (
                    <div
                      key={`${ev.at}-${i}`}
                      className="px-3 py-2 flex items-center justify-between text-sm"
                    >
                      <span className="text-slate-200">{ev.action}</span>
                      <span className="text-xs text-slate-500">{fmtRel(ev.at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
